import {Key} from "react";
import {
  Column,
  ColumnId,
  ExtractColumnContext,
  ExtractTableContext,
  ExtractTableItem,
  SortOpts,
  TableConfig,
} from "./config";
import {GroupState, SortState} from "./state";
import {typedEntries} from "../../utils/typescript";

export function resolveSortOpts<C extends Column>(column: C): Required<SortOpts> | undefined {
  if (column.compare == null) return undefined;

  const sort = column.sort;
  if (sort === false) return undefined;

  const DEFAULT_SORT_OPTS: Required<SortOpts> = {defaultDirection: "ascending"};

  if (sort == null || sort === true) {
    return DEFAULT_SORT_OPTS;
  }

  return {...DEFAULT_SORT_OPTS, ...sort};
}

export function isColumnSortable<Col extends Column>(column: Col): boolean {
  return resolveSortOpts(column) != null;
}

export function isColumnGroupable<Col extends Column>(
  column: Col,
): column is Col & {compare: NonNullable<Col["compare"]>; group: NonNullable<Col["group"]>} {
  return column.compare != null && column.group != null;
}

export function isColumnRenderable<Col extends Column>(
  column: Col,
): column is Col & {render: NonNullable<Col["render"]>} {
  return column.render != null;
}

export function isColumnHidden<Col extends Column>(column: Col, context: ExtractColumnContext<Col>): boolean {
  return column.hide?.(context) ?? false;
}

export function getRenderableColumnIds<C extends TableConfig>(config: C): ColumnId<C>[] {
  return typedEntries(config.columns)
    .filter(([_columnId, column]) => isColumnRenderable(column))
    .map(([columnId]) => columnId);
}

export function sortItems<C extends TableConfig>(
  config: C,
  sortState: SortState<C>,
  ctx: ExtractTableContext<C>,
  items: ExtractTableItem<C>[],
) {
  if (sortState == null) return items;

  const column = config.columns[sortState.columnId];

  const extract = column.extract;
  const compare = column.compare;

  if (compare == null) return items;

  return items.toSorted((a, b) => {
    const va = extract(a, ctx);
    const vb = extract(b, ctx);

    return sortState.direction === "ascending" ? compare(va, vb) : compare(vb, va);
  });
}

export type TableGroups<C extends TableConfig> = {columnId: ColumnId<C>; groups: TableGroup<C>[]};

export type TableGroup<C extends TableConfig> = {
  /** A unique key that identifies this group. */
  key: Key;

  /**
   * The full value for this group. For example, if the key is an `owner_id`,
   * this might be an `owner`. This is extracted from one of the items and used
   * to render the group header.
   */
  value: any;

  itemsOrGroups: ItemsOrGroups<C>;
};

export type ItemsOrGroups<C extends TableConfig> =
  | {type: "items"; items: ExtractTableItem<C>[]}
  | {type: "groups"; groups: TableGroup<C>[]};

export function groupItemsInner<C extends TableConfig>(
  config: C,
  groupState: GroupState<C>,
  ctx: ExtractTableContext<C>,
  items: ExtractTableItem<C>[],
  depth: number,
): ItemsOrGroups<C> {
  const UNGROUPED: ItemsOrGroups<C> = {type: "items", items};

  if (depth >= groupState.length) return UNGROUPED;
  const groupStateEntry = groupState[depth];

  const column = config.columns[groupStateEntry.columnId];

  // Column must be groupable
  if (!isColumnGroupable(column)) return UNGROUPED;

  const extractGroupUniqueKey = column.group.extractUniqueKey;
  const extract = column.extract;
  const compare = column.compare;

  type FlatGroup = Omit<TableGroup<C>, "itemsOrGroups"> & {items: ExtractTableItem<C>[]};

  // Group all the items. This is similar to `groupBy` except the full group
  // value is stored in the `TableGroup` type rather than just the key.
  const groupMap = new Map<Key, FlatGroup>();

  for (const item of items) {
    const value = extract(item, ctx);
    const groupKey = extractGroupUniqueKey(value, item, ctx) ?? "__NULL_KEY__";

    const existingGroup = groupMap.get(groupKey);

    if (existingGroup != null) {
      existingGroup.items.push(item);
    } else {
      const newGroup: FlatGroup = {key: groupKey, value: value, items: [item]};
      groupMap.set(groupKey, newGroup);
    }
  }

  // Extract the groups into an array and sort them by the column's `compare`
  // function.
  const flatGroups = [...groupMap.values()].sort((a, b) => {
    const av = a.value;
    const bv = b.value;

    return groupStateEntry.direction === "ascending" ? compare(av, bv) : compare(bv, av);
  });

  // For each group, take the flat list of items and recurse into this function,
  // grouping those and mapping them back into the parent group.
  const groups = flatGroups.map<TableGroup<C>>(intermediateGroup => ({
    key: intermediateGroup.key,
    value: intermediateGroup.value,
    itemsOrGroups: groupItemsInner(config, groupState, ctx, intermediateGroup.items, depth + 1),
  }));

  return {type: "groups", groups};
}

export function groupItems<C extends TableConfig>(
  config: C,
  ctx: ExtractTableContext<C>,
  groupState: GroupState<C>,
  items: ExtractTableItem<C>[],
): ItemsOrGroups<C> {
  return groupItemsInner(config, groupState, ctx, items, 0);
}

/**
 * Process (sort and group) a raw list of items based on the table state and
 * context.
 *
 * This is called internally by the `Table` component but you can also call it
 * yourself if you need to process the items outside of the `Table` component.
 *
 * If you don't care about the grouping, you can pass the result of this to the
 * `flattenItemsOrGroups` function to get a flat list of items.
 */
export function processTableItems<C extends TableConfig>(
  config: C,
  context: ExtractTableContext<C>,
  groupState: GroupState<C>,
  sortState: SortState<C>,
  items: ExtractTableItem<C>[],
): ItemsOrGroups<C> {
  const sortedItems = sortItems(config, sortState, context, items);
  const groupedItems = groupItems(config, context, groupState, sortedItems);

  return groupedItems;
}

export function flattenItemsOrGroups<C extends TableConfig>(itemsOrGroups: ItemsOrGroups<C>): ExtractTableItem<C>[] {
  if (itemsOrGroups.type === "items") return itemsOrGroups.items;
  return itemsOrGroups.groups.flatMap(group => flattenItemsOrGroups(group.itemsOrGroups));
}
